import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Form,
  InputNumber,
  Button,
  Row,
  Col,
  Typography,
  Select,
  message,
  Switch,
  Collapse,
  Table,
  Alert,
  Spin,
  Checkbox,
} from "antd";
import { EyeOutlined, PlusOutlined } from "@ant-design/icons";
import { Booking, Product, DeliveryInfo } from "../../../types/booking";
import { EUROPALLET } from "../../../lib/pricingDefaults";
import {
  calculateEuropalletNonExchangeFee,
  europalletInvoiceLineDescription,
} from "../../../utils/europalletFee";
import {
  calculateLmdCost,
  getLmdCountries,
  getDieselSurchargePercentage,
  getCartonsPerPallet,
  getPalletThreshold,
  getLmdRates,
  type LmdCalculation,
  type LmdRate,
} from "../../../services/lmdRatesService";
import { sendcloudService } from "../../../services/sendcloudService";
import type { SendcloudShipment } from "../../../types/sendcloud";
import SendcloudSummary from "./SendcloudSummary";

const { Text } = Typography;
const { Option } = Select;

interface LMDSectionProps {
  selectedBooking: Booking | null;
  onAddLMDLine: (
    lines: Array<{
      description: string;
      quantity: number;
      unit_price: number;
      vat_percentage: number;
      cost_type: string;
    }>
  ) => void | Promise<void>;
  /** Sync per-pallet invoice fees (outbound etc.) when transport pallet count changes */
  onTransportPalletCountChange?: (palletCount: number) => void;
  /** Sync outbound/admin fees for carton vs pallet mode (Sendcloud-aware) */
  onTransportShipmentFeesChange?: (params: {
    shipmentType: "carton" | "pallet";
    cartonCount: number;
    palletCount: number;
  }) => void | Promise<void>;
}

const LMDSection: React.FC<LMDSectionProps> = ({
  selectedBooking,
  onAddLMDLine,
  onTransportPalletCountChange,
  onTransportShipmentFeesChange,
}) => {
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);
  const [calculation, setCalculation] = useState<LmdCalculation | null>(null);
  const [loading, setLoading] = useState(false);
  const [availableRates, setAvailableRates] = useState<LmdRate[]>([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showLmdOverride, setShowLmdOverride] = useState(false);
  const [hasDeliveryAddress, setHasDeliveryAddress] = useState<boolean>(false);
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo | null>(null);
  const [sendcloudShipments, setSendcloudShipments] = useState<SendcloudShipment[]>([]);
  const [sendcloudLoading, setSendcloudLoading] = useState(false);

  const [lmdValues, setLmdValues] = useState({
    delivery_type: "auto" as "auto" | "manual" | "carton",
    apply_diesel_surcharge: true,
    diesel_surcharge_percentage: 15,
    country: "NL",
    // Manual override fields
    manual_pallets: 1,
    manual_base_rate: 0,
    manual_total_cost: 0,
    // Carton shipment override
    carton_shipment_cartons: 0,
    // Pallet override in auto mode (null = use auto-calculated value)
    pallet_override: null as number | null,
    /** Geruild = true → no fee; false → €15/pallet. Default off (niet geruild). */
    europallets_exchanged: false,
  });

  // Check if booking has delivery address
  useEffect(() => {
    if (!selectedBooking) {
      setHasDeliveryAddress(false);
      setDeliveryInfo(null);
      setShowLmdOverride(false);
      return;
    }

    // Check booking_delivery_info for delivery address
    let delivery: DeliveryInfo | null = null;
    let hasDelivery = false;

    if (selectedBooking.booking_delivery_info) {
      // Handle both array and single object cases
      if (Array.isArray(selectedBooking.booking_delivery_info)) {
        if (selectedBooking.booking_delivery_info.length > 0) {
          delivery = selectedBooking.booking_delivery_info[0];
          hasDelivery = true;
        }
      } else {
        delivery = selectedBooking.booking_delivery_info;
        hasDelivery = true;
      }
    }

    setHasDeliveryAddress(hasDelivery);
    setDeliveryInfo(delivery);

    // Only show LMD automatically if there's a delivery address
    if (!hasDelivery) {
      setShowLmdOverride(false);
    }
  }, [selectedBooking]);

  // Load Sendcloud shipments for this booking
  useEffect(() => {
    if (!selectedBooking) {
      setSendcloudShipments([]);
      return;
    }
    const fetchSendcloud = async () => {
      setSendcloudLoading(true);
      try {
        const shipments = await sendcloudService.getShipmentsForBooking(selectedBooking.id);
        setSendcloudShipments(shipments);
      } catch (err) {
        console.error("Error fetching Sendcloud shipments:", err);
      } finally {
        setSendcloudLoading(false);
      }
    };
    fetchSendcloud();
  }, [selectedBooking]);

  // Load available countries and settings
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setValidationErrors([]);
        const [countries, dieselPercentage] = await Promise.all([
          getLmdCountries(),
          getDieselSurchargePercentage(),
        ]);

        if (countries.length === 0) {
          setValidationErrors([
            "No LMD countries available. Please contact administrator.",
          ]);
          return;
        }

        setAvailableCountries(countries);

        // Update state with loaded settings
        setLmdValues((prev) => ({
          ...prev,
          country:
            countries.length > 0 && !countries.includes(prev.country)
              ? countries[0]
              : prev.country,
          diesel_surcharge_percentage: dieselPercentage,
        }));
      } catch (error) {
        console.error("Error loading LMD data:", error);
        const errorMsg =
          error instanceof Error ? error.message : "Unknown error";
        setValidationErrors([`Failed to load LMD configuration: ${errorMsg}`]);
        message.error("Failed to load LMD configuration");
      }
    };
    loadInitialData();
  }, []);

  // Load rates for selected country
  useEffect(() => {
    const loadRatesForCountry = async () => {
      if (!lmdValues.country) return;

      setRatesLoading(true);
      setAvailableRates([]); // clear so calc waits for this country's rates
      try {
        const rates = await getLmdRates(lmdValues.country, "ex_diesel");
        setAvailableRates(rates);
      } catch (error) {
        console.error("Error loading rates for country:", error);
        setAvailableRates([]);
      } finally {
        setRatesLoading(false);
      }
    };

    loadRatesForCountry();
  }, [lmdValues.country]);

  // Helper to get booking products in consistent format
  const getBookingProducts = (booking: Booking): Product[] => {
    if (!booking.product_info) return [];

    // Handle different structures of product_info from booking_product table
    if (Array.isArray(booking.product_info)) {
      // Already an array of products
      return booking.product_info as Product[];
    }

    // Single product
    return [booking.product_info as Product];
  };

  // Calculate total cartons with smart priority
  const getTotalCartonsFromBooking = (): number => {
    if (!selectedBooking) return 0;

    // Priority 1: Use delivery_cartons from specific delivery
    const deliveryInfo = Array.isArray(selectedBooking.booking_delivery_info)
      ? selectedBooking.booking_delivery_info[0]
      : selectedBooking.booking_delivery_info;

    if (deliveryInfo?.delivery_cartons && deliveryInfo.delivery_cartons > 0) {
      return deliveryInfo.delivery_cartons;
    }

    // Priority 2: Use total_actual_cartons from container unloading
    if (
      selectedBooking.total_actual_cartons &&
      selectedBooking.total_actual_cartons > 0
    ) {
      return selectedBooking.total_actual_cartons;
    }

    // Priority 3: Fallback to estimated cartons from product_info
    if (!selectedBooking.product_info) return 0;

    const products = getBookingProducts(selectedBooking);
    return products.reduce((total, product) => {
      return total + (product?.cartons || 0);
    }, 0);
  };

  /** Prefer stored delivery/unloading pallets or own-delivery/pallet shipment count. */
  const getStoredPalletCount = (): number | null => {
    if (!selectedBooking) return null;

    const deliveryInfo = Array.isArray(selectedBooking.booking_delivery_info)
      ? selectedBooking.booking_delivery_info[0]
      : selectedBooking.booking_delivery_info;

    if (deliveryInfo?.delivery_pallets && deliveryInfo.delivery_pallets > 0) {
      return deliveryInfo.delivery_pallets;
    }

    if (
      selectedBooking.total_actual_pallets &&
      selectedBooking.total_actual_pallets > 0
    ) {
      return selectedBooking.total_actual_pallets;
    }

    const activeShipments = sendcloudShipments.filter(
      (s) => s.status !== "cancelled",
    );
    if (activeShipments.length === 0) return null;

    const isPalletShipment = activeShipments.every((s) => {
      const carrier = (s.carrier_name || "").toLowerCase();
      const method = (s.shipping_method || "").toLowerCase();
      return (
        carrier === "efreight" ||
        carrier.includes("transmission") ||
        method === "own delivery" ||
        method.includes("transmission") ||
        method.includes("pallet")
      );
    });

    if (isPalletShipment) return activeShipments.length;
    return null;
  };

  /** Carton count from Sendcloud labels (one DB row per parcel/carton for parcel carriers). */
  const getSendcloudCartonCount = (): number | null => {
    const activeShipments = sendcloudShipments.filter(
      (s) => s.status !== "cancelled",
    );
    if (activeShipments.length === 0) return null;

    const isPalletShipment = activeShipments.every((s) => {
      const carrier = (s.carrier_name || "").toLowerCase();
      const method = (s.shipping_method || "").toLowerCase();
      return (
        carrier === "efreight" ||
        carrier.includes("transmission") ||
        method === "own delivery" ||
        method.includes("transmission") ||
        method.includes("pallet")
      );
    });
    if (isPalletShipment) return null;

    const multiColloContext = activeShipments[0].sendcloud_data as Record<
      string,
      unknown
    > | null;
    const scTotalCartons = (
      multiColloContext?.multi_parcel_context as Record<string, unknown>
    )?.total_cartons as number | undefined;

    return scTotalCartons && scTotalCartons > 0
      ? scTotalCartons
      : activeShipments.length;
  };

  // Auto-recalculate in manual mode when pallets change
  const handleManualPalletChange = async (pallets: number) => {
    setLmdValues((prev) => ({ ...prev, manual_pallets: pallets }));

    // Trigger recalculation to get the correct base rate from database
    if (pallets > 0 && selectedBooking) {
      // Small delay to ensure state is updated
      setTimeout(() => {
        performLmdCalculation();
      }, 100);
    }
  };

  // Enhanced validation
  const validateLmdInputs = (): string[] => {
    const errors: string[] = [];

    if (!selectedBooking) {
      errors.push("Please select a booking first");
    }

    if (!lmdValues.country) {
      errors.push("Please select a delivery country");
    }

    if (
      (lmdValues.delivery_type === "auto" ||
        lmdValues.delivery_type === "carton") &&
      availableRates.length === 0
    ) {
      errors.push(`No rates available for ${lmdValues.country}`);
    }

    const totalCartons = getTotalCartonsFromBooking();
    const storedPallets = getStoredPalletCount();
    if (
      (lmdValues.delivery_type === "auto" ||
        lmdValues.delivery_type === "carton") &&
      totalCartons === 0 &&
      !(lmdValues.delivery_type === "auto" && storedPallets != null)
    ) {
      errors.push("Booking has no cartons. Please check product information.");
    }

    if (lmdValues.delivery_type === "manual") {
      if (lmdValues.manual_pallets < 1) {
        errors.push("Manual pallet count must be at least 1");
      }
      if (lmdValues.manual_total_cost <= 0) {
        errors.push("Manual total cost must be greater than 0");
      }
    }

    return errors;
  };

  // Calculate LMD cost using database service
  const performLmdCalculation = async (): Promise<void> => {
    if (!selectedBooking) return;

    const errors = validateLmdInputs();
    setValidationErrors(errors);

    if (errors.length > 0) {
      setCalculation(null);
      return;
    }

    setLoading(true);
    try {
      if (lmdValues.delivery_type === "manual") {
        // Use database rates to calculate pallet pricing
        const result = await calculateLmdCost(
          lmdValues.country,
          lmdValues.manual_pallets,
          0, // No carton count for manual pallet mode
          lmdValues.apply_diesel_surcharge,
          lmdValues.diesel_surcharge_percentage,
          false // Force pallet rate (not carton rate)
        );

        // Update the manual values with the calculated rates
        setLmdValues((prev) => ({
          ...prev,
          manual_base_rate: Number(result.baseRate.toFixed(2)),
          manual_total_cost: Number(result.totalRate.toFixed(2)),
        }));

        setCalculation(result);
        setValidationErrors([]);
        setLoading(false);
        return;
      }

      // Auto calculation mode or carton shipment mode
      let totalCartons = getTotalCartonsFromBooking();
      let palletCount = 1;
      let forceCartonRate = false;

      if (lmdValues.delivery_type === "carton") {
        // Force carton rate and use custom carton count if specified
        forceCartonRate = true;
        palletCount = 1;
        if (lmdValues.carton_shipment_cartons > 0) {
          totalCartons = lmdValues.carton_shipment_cartons;
        }
      } else if (lmdValues.pallet_override != null) {
        // Auto mode with user override
        palletCount = lmdValues.pallet_override;
      } else {
        const storedPallets = getStoredPalletCount();
        if (storedPallets != null) {
          palletCount = storedPallets;
        } else {
          // Auto mode - smart detection from cartons
          const [cartonsPerPallet, palletThreshold] = await Promise.all([
            getCartonsPerPallet(),
            getPalletThreshold(),
          ]);

          if (totalCartons > palletThreshold) {
            palletCount = Math.ceil(totalCartons / cartonsPerPallet);
          } else {
            forceCartonRate = true;
            palletCount = 1;
          }
        }
      }

      const result = await calculateLmdCost(
        lmdValues.country,
        palletCount,
        totalCartons,
        lmdValues.apply_diesel_surcharge,
        lmdValues.diesel_surcharge_percentage,
        forceCartonRate
      );

      setCalculation(result);
      setValidationErrors([]);
    } catch (error) {
      console.error("Error calculating LMD cost:", error);
      const errorMsg = error instanceof Error ? error.message : "Unknown error";
      setValidationErrors([`Calculation failed: ${errorMsg}`]);
      message.error("Failed to calculate LMD cost");
      setCalculation(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDeliveryTypeChange = async (value: "auto" | "manual" | "carton") => {
    const reenableDiesel = (prev: typeof lmdValues) =>
      prev.delivery_type === "carton" ? true : prev.apply_diesel_surcharge;

    if (value === "manual") {
      setLmdValues((prev) => ({
        ...prev,
        delivery_type: value,
        manual_pallets: calculation?.palletCount ?? 1,
        manual_base_rate: calculation ? Number(calculation.baseRate.toFixed(2)) : 0,
        manual_total_cost: calculation ? Number(calculation.totalRate.toFixed(2)) : 0,
        carton_shipment_cartons: 0,
        pallet_override: null,
        apply_diesel_surcharge: reenableDiesel(prev),
      }));
    } else if (value === "carton") {
      const cartons = getTotalCartonsFromBooking();
      setLmdValues((prev) => ({
        ...prev,
        delivery_type: value,
        carton_shipment_cartons: cartons > 0 ? cartons : 1,
        pallet_override: null,
        apply_diesel_surcharge: false,
      }));
    } else {
      setLmdValues((prev) => ({
        ...prev,
        delivery_type: value,
        carton_shipment_cartons: 0,
        pallet_override: null,
        apply_diesel_surcharge: reenableDiesel(prev),
      }));
      setCalculation(null);
    }
  };

  // Recalculate when booking/settings change — and when rates finish loading.
  // Without availableRates/ratesLoading in deps, calc often ran with [] rates,
  // stuck on "No rates available" while the rate table later showed 16 rows.
  useEffect(() => {
    if (!selectedBooking || availableCountries.length === 0) return;
    if (ratesLoading) return;
    performLmdCalculation();
  }, [
    selectedBooking,
    lmdValues.delivery_type,
    lmdValues.apply_diesel_surcharge,
    lmdValues.diesel_surcharge_percentage,
    lmdValues.country,
    lmdValues.manual_pallets,
    lmdValues.manual_base_rate,
    lmdValues.manual_total_cost,
    lmdValues.carton_shipment_cartons,
    lmdValues.pallet_override,
    availableCountries,
    availableRates,
    ratesLoading,
    sendcloudShipments,
  ]);

  // Keep outbound / other per-pallet fees aligned with LMD transport pallet count
  const onPalletCountChangeRef = useRef(onTransportPalletCountChange);
  onPalletCountChangeRef.current = onTransportPalletCountChange;
  const onShipmentFeesChangeRef = useRef(onTransportShipmentFeesChange);
  onShipmentFeesChangeRef.current = onTransportShipmentFeesChange;

  useEffect(() => {
    if (!calculation) return;

    if (calculation.isCartonPricing) {
      const sendcloudCartons = getSendcloudCartonCount();
      const cartonCount =
        sendcloudCartons ??
        (calculation.cartonCount > 0 ? calculation.cartonCount : getTotalCartonsFromBooking());
      onShipmentFeesChangeRef.current?.({
        shipmentType: "carton",
        cartonCount: Math.max(1, cartonCount),
        palletCount: 0,
      });
      return;
    }

    if (calculation.palletCount >= 1) {
      // Use LMD pallet count (not Sendcloud shipment rows — Own Delivery is often 1 label for N pallets).
      const palletCount = calculation.palletCount;
      onShipmentFeesChangeRef.current?.({
        shipmentType: "pallet",
        cartonCount: 0,
        palletCount,
      });
      onPalletCountChangeRef.current?.(palletCount);
    }
  }, [calculation, sendcloudShipments]);

  // Add LMD to invoice lines (separate lines for base, diesel, and NL pallet surcharge)
  const addLMDToInvoiceLines = async () => {
    if (!calculation) {
      message.error("Please calculate LMD cost first");
      return;
    }

    if (validationErrors.length > 0) {
      message.error("Please fix validation errors first");
      return;
    }

    const baseDescription = calculation.description
      .replace(/ \+ \d+% diesel/g, "")
      .replace(/ \+ €[\d.]+\/pallet (?:NL toeslag|tol|Tol surcharge) \(\d+\)/g, "");

    const lmdLines: Array<{
      description: string;
      quantity: number;
      unit_price: number;
      vat_percentage: number;
      cost_type: string;
    }> = [
      {
        description: baseDescription,
        quantity: 1,
        unit_price: Number(calculation.baseRate.toFixed(2)),
        vat_percentage: 0,
        cost_type: "lmd_delivery",
      },
    ];

    if (calculation.dieselSurcharge > 0) {
      lmdLines.push({
        description: `LMD Diesel Surcharge (${lmdValues.diesel_surcharge_percentage}%)`,
        quantity: 1,
        unit_price: Number(calculation.dieselSurcharge.toFixed(2)),
        vat_percentage: 0,
        cost_type: "lmd_diesel_surcharge",
      });
    }

    if (calculation.palletSurcharge > 0) {
      lmdLines.push({
        description: `Tol surcharge (€${calculation.palletSurchargePerPallet.toFixed(2)}/pallet × ${calculation.palletCount})`,
        quantity: 1,
        unit_price: Number(calculation.palletSurcharge.toFixed(2)),
        vat_percentage: 0,
        cost_type: "lmd_nl_pallet_surcharge",
      });
    }

    const isPalletShipment =
      !calculation.isCartonPricing && calculation.palletCount >= 1;
    const addEuropalletLine =
      isPalletShipment && !lmdValues.europallets_exchanged;
    if (addEuropalletLine) {
      const fee = calculateEuropalletNonExchangeFee(
        calculation.palletCount,
        false,
      );
      if (fee > 0) {
        // Separate invoice line: qty = pallets, unit = €15
        lmdLines.push({
          description: europalletInvoiceLineDescription(),
          quantity: calculation.palletCount,
          unit_price: EUROPALLET.FEE_PER_PALLET,
          vat_percentage: 0,
          cost_type: EUROPALLET.COST_TYPE,
        });
      }
    }

    // Await so outbound sync cannot overwrite before LMD lines are persisted.
    await onAddLMDLine(lmdLines);

    if (!calculation.isCartonPricing && calculation.palletCount >= 1) {
      await onTransportShipmentFeesChange?.({
        shipmentType: "pallet",
        cartonCount: 0,
        palletCount: calculation.palletCount,
      });
    } else if (calculation.isCartonPricing) {
      const sendcloudCartons = getSendcloudCartonCount();
      const cartonCount =
        sendcloudCartons ??
        (calculation.cartonCount > 0 ? calculation.cartonCount : getTotalCartonsFromBooking());
      await onTransportShipmentFeesChange?.({
        shipmentType: "carton",
        cartonCount: Math.max(1, cartonCount),
        palletCount: 0,
      });
    }

    message.success(
      addEuropalletLine
        ? `LMD added, plus separate europallet line (${calculation.palletCount} × €${EUROPALLET.FEE_PER_PALLET})`
        : "Last Mile Delivery cost added to invoice",
    );
    setCalculation(null);
  };

  if (!selectedBooking) {
    return null;
  }

  if (!hasDeliveryAddress && !showLmdOverride) {
    return (
      <Alert
        message="No delivery address — booking goes to storage"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        action={
          <Button size="small" icon={<PlusOutlined />} onClick={() => setShowLmdOverride(true)}>
            Add LMD anyway
          </Button>
        }
      />
    );
  }

  const totalCartons = getTotalCartonsFromBooking();

  // Prepare rate table data
  const rateTableData = availableRates.map((rate, index) => ({
    key: index,
    pallets: rate.pallets,
    rate: rate.rate_eur,
    description:
      rate.pallets === 0
        ? "Per carton"
        : `${rate.pallets} pallet${rate.pallets !== 1 ? "s" : ""}`,
    notes: rate.notes,
  }));

  const rateTableColumns = [
    {
      title: "Pallets",
      dataIndex: "pallets",
      key: "pallets",
      width: 80,
      render: (pallets: number) => (pallets === 0 ? "Carton" : pallets),
    },
    {
      title: "Rate (€)",
      dataIndex: "rate",
      key: "rate",
      width: 100,
      render: (rate: number) => `€${rate}`,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
  ];

  const europalletFee =
    calculation &&
    !lmdValues.europallets_exchanged &&
    !calculation.isCartonPricing &&
    calculation.palletCount >= 1
      ? calculateEuropalletNonExchangeFee(calculation.palletCount, false)
      : 0;
  const lmdBaseTotal =
    lmdValues.delivery_type === "auto" || lmdValues.delivery_type === "carton"
      ? calculation?.totalRate || 0
      : lmdValues.manual_total_cost;
  const displayedLmdTotal = Number((lmdBaseTotal + europalletFee).toFixed(2));

  return (
    <Card
      size="small"
      style={{
        marginBottom: 16,
        border: "2px solid #722ed1",
        backgroundColor: "#f9f0ff",
      }}
      title={
        <span style={{ color: "#722ed1", fontWeight: "bold" }}>
          🚚 Last Mile Delivery (LMD) Calculation
          {deliveryInfo && (
            <span
              style={{ fontSize: "12px", fontWeight: "normal", marginLeft: 8 }}
            >
              → {deliveryInfo.city}, {deliveryInfo.country}
            </span>
          )}
        </span>
      }
      extra={
        !hasDeliveryAddress && (
          <Button
            size="small"
            onClick={() => setShowLmdOverride(false)}
            type="text"
          >
            Hide Override
          </Button>
        )
      }
    >
      {validationErrors.length > 0 && (
        <Alert message={validationErrors[0]} type="warning" showIcon style={{ marginBottom: 8, fontSize: 12 }} />
      )}

      {hasDeliveryAddress && deliveryInfo && (
        <Text style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
          {totalCartons} cartons · Delivery to <Text strong>{deliveryInfo.street_name} {deliveryInfo.house_number}, {deliveryInfo.city}</Text>
        </Text>
      )}

      {/* Sendcloud shipment comparison */}
      {sendcloudLoading ? (
        <div style={{ marginBottom: 8, fontSize: 12 }}>
          <Spin size="small" /> <Text type="secondary" style={{ fontSize: 12 }}>Loading Sendcloud shipments...</Text>
        </div>
      ) : sendcloudShipments.length > 0 && (
        <SendcloudSummary
          shipments={sendcloudShipments}
          proposedPallets={calculation?.palletCount}
          proposedCartons={totalCartons}
        />
      )}

      <Row gutter={[8, 0]} align="bottom">
        <Col span={3}>
          <Form.Item label="Country" style={{ marginBottom: 0 }}>
            <Select
              value={lmdValues.country}
              onChange={(value) =>
                setLmdValues((prev) => ({ ...prev, country: value }))
              }
              size="small"
              loading={loading}
            >
              {availableCountries.map((country) => (
                <Option key={country} value={country}>
                  {country}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={4}>
          <Form.Item label="Mode" style={{ marginBottom: 0 }}>
            <Select
              value={lmdValues.delivery_type}
              onChange={handleDeliveryTypeChange}
              size="small"
            >
              <Option value="auto">Auto</Option>
              <Option value="carton">Per Carton</Option>
              <Option value="manual">Per Pallet</Option>
            </Select>
          </Form.Item>
        </Col>

        {lmdValues.delivery_type === "carton" ||
        (lmdValues.delivery_type === "auto" &&
          calculation &&
          calculation.isCartonPricing) ? (
          <Col span={3}>
            <Form.Item label="Cartons" style={{ marginBottom: 0 }}>
              <InputNumber
                value={
                  lmdValues.delivery_type === "carton"
                    ? lmdValues.carton_shipment_cartons || totalCartons
                    : calculation?.cartonCount || totalCartons
                }
                onChange={(value) =>
                  setLmdValues((prev) => ({
                    ...prev,
                    carton_shipment_cartons: Number(value) || totalCartons,
                  }))
                }
                disabled={lmdValues.delivery_type === "auto"}
                min={1}
                max={999}
                size="small"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        ) : (
          <Col span={3}>
            <Form.Item label="Pallets" style={{ marginBottom: 0 }}>
              <InputNumber
                value={
                  lmdValues.delivery_type === "auto"
                    ? (lmdValues.pallet_override ?? calculation?.palletCount ?? 0)
                    : lmdValues.manual_pallets
                }
                onChange={(value) => {
                  const v = value || 1;
                  if (lmdValues.delivery_type === "auto") {
                    setLmdValues((prev) => ({ ...prev, pallet_override: v }));
                  } else {
                    handleManualPalletChange(v);
                  }
                }}
                min={1}
                max={50}
                size="small"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        )}

        {lmdValues.delivery_type !== "carton" &&
          !(lmdValues.delivery_type === "auto" && calculation?.isCartonPricing) && (
            <Col span={3}>
              <Form.Item
                label={`Diesel ${lmdValues.diesel_surcharge_percentage}%`}
                style={{ marginBottom: 0 }}
              >
                <Switch
                  checked={lmdValues.apply_diesel_surcharge}
                  onChange={(checked) =>
                    setLmdValues((prev) => ({
                      ...prev,
                      apply_diesel_surcharge: checked,
                    }))
                  }
                  size="small"
                />
              </Form.Item>
            </Col>
          )}

        {lmdValues.delivery_type !== "carton" &&
          !(lmdValues.delivery_type === "auto" && calculation?.isCartonPricing) && (
            <Col span={5}>
              <Form.Item
                label="Europallets geruild"
                style={{ marginBottom: 0 }}
                tooltip="Checked = geruild, no fee. Unchecked = niet geruild → separate invoice line at €15/pallet when you Add to Invoice."
              >
                <Checkbox
                  checked={lmdValues.europallets_exchanged}
                  onChange={(e) =>
                    setLmdValues((prev) => ({
                      ...prev,
                      europallets_exchanged: e.target.checked,
                    }))
                  }
                >
                  <Text style={{ fontSize: 12 }}>
                    {lmdValues.europallets_exchanged
                      ? "Ja"
                      : `Nee (€${EUROPALLET.FEE_PER_PALLET}/pl)`}
                  </Text>
                </Checkbox>
              </Form.Item>
            </Col>
          )}

        <Col flex="auto" style={{ minWidth: 120 }}>
          <Form.Item label="Total" style={{ marginBottom: 0 }}>
            <InputNumber
              value={displayedLmdTotal}
              readOnly
              min={0}
              precision={2}
              size="small"
              prefix="€"
              style={{ width: "100%", fontWeight: 600 }}
            />
          </Form.Item>
        </Col>
      </Row>

      {calculation && (
        <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
          €{calculation.baseRate.toFixed(2)} base
          {calculation.dieselSurcharge > 0 &&
            ` + €${calculation.dieselSurcharge.toFixed(2)} diesel`}
          {calculation.palletSurcharge > 0 &&
            ` + €${calculation.palletSurcharge.toFixed(2)} tol`}
          {europalletFee > 0 &&
            ` + €${europalletFee.toFixed(2)} europallets`}
        </Text>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
        <Button
          type="primary"
          size="small"
          onClick={addLMDToInvoiceLines}
          disabled={!calculation || loading || validationErrors.length > 0}
          loading={loading}
        >
          Add to Invoice
        </Button>
      </div>

      {/* Collapsible rate table */}
      {availableRates.length > 0 && (
        <Collapse
          size="small"
          style={{ marginTop: 8 }}
          items={[
            {
              key: "rates",
              label: (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  <EyeOutlined /> {lmdValues.country} rate table ({availableRates.length} rates)
                </Text>
              ),
              children: (
                <Table
                  dataSource={rateTableData}
                  columns={rateTableColumns}
                  size="small"
                  pagination={false}
                  loading={ratesLoading}
                  style={{ fontSize: 11 }}
                  scroll={{ y: 200 }}
                />
              ),
            },
          ]}
        />
      )}
    </Card>
  );
};

export default LMDSection;
